import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Cloud as CloudIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import {
  setAccountFarm,
} from '../../../actions/accountFarmAction';
import { setCurrentSector } from '../../../actions/currentSectorActions';
import CustomHeader from '../../../components/General/CustomHeader';
import Page from '../../../components/Layout/Page';
import apiClient from '../../../requests/api/apiClient';
import {
  FarmSchema,
  SectorSchema,
  SensorModelSchema,
} from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import useChartFilter from '../../../hooks/useChartFilter';
import WeatherChartsTab from '../Layout/SectorDetail/component/SectorCharts/WeatherChartsTab';
import WeatherStationDashboard from './WeatherStationDashboard';
import StationInfoSidebar from './StationInfoSidebar';
import AddWeatherStationModal from './AddWeatherStationModal';
import useStyles from './styles';

const WeatherStationDetail = () => {
  const classes = useStyles();
  const { stationId } = useParams<{ stationId: string }>();
  const history = useHistory();
  const dispatch = useDispatch();

  const { selectedFarm } = useSelector(
    (state: ReduxState) => state.accountFarm,
  );
  const { selectedAccount } = useSelector(
    (state: ReduxState) => state.userAccount,
  );

  const { enqueueSnackbar } = useSnackbar();

  const [station, setStation] = useState<SensorModelSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'clima' | 'todos'>(
    'clima',
  );

  const {
    tempStartDate,
    tempEndDate,
    selectedInterval,
    setTempStartDate,
    setTempEndDate,
    handleApplyDates,
    handleIntervalSelect,
  } = useChartFilter();

  useEffect(() => {
    if (!stationId) {
      setLoading(false);
      return;
    }
    const fetchStation = async () => {
      try {
        setLoading(true);
        const { data } = await apiClient.sensorModels
          .sensorModelsDetail(stationId);
        const stationData = data?.sensorModel;
        if (!stationData) {
          history.push('/inicio');
          return;
        }
        setStation(stationData);
      } catch {
        history.push('/inicio');
      } finally {
        setLoading(false);
      }
    };
    fetchStation();
  }, [stationId]);

  const fedSectors = useMemo(() => {
    if (!station?.zoneId || !selectedFarm?.sectors) return [];
    return selectedFarm.sectors.filter(
      (s) => s.weatherZoneId === station.zoneId,
    ) as SectorSchema[];
  }, [station?.zoneId, selectedFarm?.sectors]);

  const farmId = selectedFarm?.id || null;
  const sectorId = fedSectors.length > 0
    ? fedSectors[0].id : null;

  useEffect(() => {
    if (sectorId || !station?.zoneId || !selectedAccount?.id
      || !selectedFarm?.id) return;
    const refreshFarm = async () => {
      try {
        const { data } = await apiClient.farms
          .accountsDetail(selectedAccount.id as string);
        const freshFarm = data?.allFarms?.find(
          (f) => f.id === selectedFarm.id,
        );
        if (freshFarm) {
          dispatch(
            setAccountFarm(freshFarm as FarmSchema),
          );
        }
      } catch {
        // silent — fallback to cached data
      }
    };
    refreshFarm();
  }, [sectorId, station?.zoneId, selectedAccount?.id,
    selectedFarm?.id]);

  useEffect(() => {
    if (!sectorId) return;
    const fetchSector = async () => {
      const sectorResponse = await apiClient.sector
        .sectorDetail(sectorId);
      if (sectorResponse.data?.sector) {
        dispatch(
          setCurrentSector(
            sectorResponse.data.sector as SectorSchema,
          ),
        );
      }
    };
    fetchSector();
  }, [sectorId]);

  const breadcrumb = stationId
    ? `${selectedFarm?.name || ''} / ${station?.name || ''}`
    : `${selectedFarm?.name || ''} / Estaciones de clima`;
  const sidebarPaddingRight = sidebarCollapsed ? '0px' : '310px';

  if (!stationId) {
    return (
      <Page className={classes.root} title="Estaciones de clima">
        <Container maxWidth={false}>
          <Box
            height="100%"
            display="flex"
            flexDirection="column"
            pt={0.5}
          >
            <CustomHeader
              title={breadcrumb}
              showBackButton={true}
              onBack={() => history.push('/inicio')}
            />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="400px"
              gap={2}
            >
              <CloudIcon
                sx={{ fontSize: 64, color: 'text.secondary' }}
              />
              <Typography variant="h4" color="textSecondary">
                No hay estaciones de clima
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
              >
                Crea tu primera estación de clima para
                comenzar a monitorear datos meteorológicos.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setAddModalOpen(true)}
              >
                Agregar Estación de Clima
              </Button>
            </Box>
          </Box>
        </Container>
        <AddWeatherStationModal
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          onSuccess={(sensorModelId) => {
            setAddModalOpen(false);
            if (sensorModelId) {
              history.push(
                `/weather-station/${sensorModelId}`,
              );
            } else {
              window.location.reload();
            }
          }}
        />
      </Page>
    );
  }

  if (loading) {
    return (
      <Page className={classes.root} title="Estación de clima">
        <Container maxWidth={false}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  return (
    <Page className={classes.root} title={breadcrumb}>
      <Container maxWidth={false}>
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          pt={0.5}
          sx={{
            paddingRight: sidebarPaddingRight,
            transition: 'padding-right 0.2s ease',
          }}
        >
          <CustomHeader
            title={breadcrumb}
            showBackButton={true}
            onBack={() => history.push('/inicio')}
          />
          <Box mt={2} width="98%">
            {!(sectorId && farmId) && (
              <Typography variant="body1">
                No se encontró un sector asociado a esta estación
              </Typography>
            )}
            {sectorId && farmId && viewMode === 'clima' && (
              <WeatherStationDashboard
                sectorId={sectorId}
                farmId={farmId}
              />
            )}
            {sectorId && farmId && viewMode === 'todos' && (
              <WeatherChartsTab
                hideKc={true}
                sectorId={sectorId}
                farmId={farmId}
              />
            )}
          </Box>
        </Box>
      </Container>
      {station && (
        <StationInfoSidebar
          station={station}
          fedSectors={fedSectors}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          selectedInterval={selectedInterval}
          tempStartDate={tempStartDate}
          tempEndDate={tempEndDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onIntervalSelect={handleIntervalSelect}
          onStartDateChange={setTempStartDate}
          onEndDateChange={setTempEndDate}
          onApplyDates={handleApplyDates}
          onAddStation={() => setAddModalOpen(true)}
          onDeleteStation={() => setDeleteDialogOpen(true)}
        />
      )}
      <AddWeatherStationModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={(sensorModelId) => {
          setAddModalOpen(false);
          if (sensorModelId) {
            history.push(
              `/weather-station/${sensorModelId}`,
            );
          } else {
            window.location.reload();
          }
        }}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Eliminar Estación de Clima</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar la estación
            &quot;{station?.name}&quot;? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              try {
                await apiClient.sensorModels
                  .sensorModelsDelete(stationId);
                enqueueSnackbar(
                  'Estación eliminada exitosamente',
                  { variant: 'success' },
                );
                setDeleteDialogOpen(false);
                history.push('/inicio');
              } catch {
                enqueueSnackbar(
                  'Error al eliminar la estación',
                  { variant: 'error' },
                );
              }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default WeatherStationDetail;
